import { useState } from 'react';
import { useGT } from 'gt-react';
import { Combobox } from '@/components/ui/combobox';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import { useItemCatalog } from 'src/api/rpc/hooks/inventory-management/useItemCatalog';
import { buildProductOptions, type ProductOption } from './productOptions';

function ProductOptionRow({ option }: { option: ProductOption }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="truncate text-sm font-medium">
        {option.displaySku ?? option.key}
      </span>
      {option.displayDescription && (
        <span className="text-muted-foreground truncate text-xs">
          {option.displayDescription}
        </span>
      )}
    </div>
  );
}

/**
 * Searchable ProductV3 picker backed by the item catalog's server-side search
 * (`listCatalog`), the same read the inventory catalog page uses. The value is
 * the ProductV3 key — the SKU the procurement contracts speak.
 */
export function ProductPickerCombobox({
  value,
  onChange,
  excludeKey,
  placeholder,
  allowClear = false,
  className,
  'aria-label': ariaLabel,
}: {
  value: string | null;
  onChange: (key: string | null) => void;
  /** A product that is never a valid pick, such as the page's own. */
  excludeKey?: string;
  placeholder?: string;
  allowClear?: boolean;
  className?: string;
  'aria-label'?: string;
}) {
  const gt = useGT();
  // Two states for one box: what the buyer has typed (rendered immediately) and
  // the term the catalog is actually queried with (`onSearch`, debounced).
  const [searchValue, setSearchValue] = useState('');
  const [search, setSearch] = useState('');

  const catalog = useItemCatalog(
    { search, searchMode: 'text', page: 1, pageSize: 25 },
    { enabled: search.trim().length > 0 },
  );

  const options = buildProductOptions(
    catalog.data?.edges ?? [],
    excludeKey,
    value,
  );

  const emptyMessage = catalog.isFetching
    ? gt('Searching…', {
        context: 'loading state, catalog product picker',
      })
    : search.trim().length === 0
      ? gt('Type to search the catalog.', {
          context: 'hint, catalog product picker',
        })
      : gt('No products found.', {
          context: 'empty state, catalog product picker',
        });

  return (
    <Combobox
      items={options}
      value={value || null}
      onValueChange={(key) => onChange(key)}
      deselectable
      getValue={(option) => option.key}
      getLabel={(option) => option.displaySku ?? option.key}
      // The catalog already searched SKU and description server-side, so
      // `onSearch` turns the client re-filter (label only) off.
      search={searchValue}
      onSearchChange={setSearchValue}
      onSearch={setSearch}
      debounceMs={250}
      pinSelected={false}
      resetSearchOnClose={false}
      indicator="none"
      renderItem={(option) => <ProductOptionRow option={option} />}
      allowClear={allowClear}
      className={className}
      contentClassName="w-full"
      aria-label={ariaLabel}
      labels={pickerLabels({
        placeholder:
          placeholder ??
          gt('Select a product…', {
            context: 'placeholder, catalog product picker',
          }),
        search: gt('Search by SKU or description…', {
          context: 'placeholder, catalog product search',
        }),
        empty: emptyMessage,
      })}
    />
  );
}
